import React from 'react';
import { useTranslation } from 'react-i18next';
import { theme } from '../utils/theme';

const GuideSection = ({ icon, title, description, items, reasoning }) => (
  <div className="mb-8 p-6 bg-white rounded-lg shadow-sm border border-[#6D9773]/20 hover:shadow-md transition-all duration-200">
    <div className="flex items-center gap-4 mb-4">
      <div className="w-12 h-12 flex items-center justify-center">
        <img 
          src={`/card-icons/${icon}`} 
          alt="" 
          className="w-10 h-10"
          style={{ filter: 'invert(47%) sepia(93%) saturate(341%) hue-rotate(93deg) brightness(88%) contrast(86%)' }}
        />
      </div>
      <div>
        <h3 className={theme.text.h3}>{title}</h3>
        {description && <p className={`${theme.text.body} text-gray-600 mt-1`}>{description}</p>}
      </div>
    </div>
    <div className={`${theme.text.body} space-y-4`}>
      <ul className="list-disc pl-5 space-y-3">
        {items.map((item, index) => (
          <li key={index} className="text-[#0C3B2E]">{item}</li>
        ))}
      </ul>
      {reasoning && (
        <div className="mt-4 pt-4 border-t border-[#6D9773]/20">
          <p className="text-[#0C3B2E]/80 italic">
            <span className="font-semibold">Why this matters: </span>
            {reasoning}
          </p>
        </div>
      )}
    </div>
  </div>
);

const PhotoGuideStep = ({ onBack }) => {
  const { t } = useTranslation('guide');

  const handleBack = () => {
    if (onBack) {
      onBack();
    }
  };

  return (
    <div className="max-w-4xl mx-auto pb-12">
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={handleBack}
          className={`${theme.button.base} ${theme.button.secondary} ${theme.button.sizes.md} flex items-center gap-2`}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          {t('back')}
        </button>
        <h2 className={theme.text.h2}>{t('title')}</h2>
      </div>

      <div className="space-y-6">
        <div className={`${theme.card.base} bg-[#0C3B2E]/5 mb-8`}>
          <div className="flex items-start gap-4">
            <svg className="w-6 h-6 text-[#0C3B2E] mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className={`${theme.text.h3} mb-2`}>{t('intro.title')}</h3>
              <p className={theme.text.body}>{t('intro.description')}</p>
            </div>
          </div>
        </div>

        <GuideSection
          icon="smile-with-teeth.svg"
          title={t('sections.facial.title')}
          description={t('sections.facial.description')}
          items={t('sections.facial.items', { returnObjects: true })}
          reasoning={t('sections.facial.reasoning')}
        />

        <GuideSection
          icon="open-eyes.svg"
          title={t('sections.eyes.title')}
          description={t('sections.eyes.description')}
          items={t('sections.eyes.items', { returnObjects: true })}
          reasoning={t('sections.eyes.reasoning')}
        />

        <GuideSection
          icon="natural-head-position.svg"
          title={t('sections.head.title')}
          description={t('sections.head.description')}
          items={t('sections.head.items', { returnObjects: true })}
          reasoning={t('sections.head.reasoning')}
        />

        <GuideSection
          icon="full-body.svg"
          title={t('sections.body.title')}
          description={t('sections.body.description')}
          items={t('sections.body.items', { returnObjects: true })}
          reasoning={t('sections.body.reasoning')}
        />

        <GuideSection
          icon="hands.svg"
          title={t('sections.hands.title')}
          description={t('sections.hands.description')}
          items={t('sections.hands.items', { returnObjects: true })}
          reasoning={t('sections.hands.reasoning')}
        />

        <GuideSection
          icon="crowed-background.svg"
          title={t('sections.background.title')}
          description={t('sections.background.description')}
          items={t('sections.background.items', { returnObjects: true })}
          reasoning={t('sections.background.reasoning')}
        />

        <GuideSection
          icon="lights.svg"
          title={t('sections.lighting.title')}
          description={t('sections.lighting.description')}
          items={t('sections.lighting.items', { returnObjects: true })}
          reasoning={t('sections.lighting.reasoning')}
        />

        <GuideSection
          icon="motion-blur.svg"
          title={t('sections.clarity.title')}
          description={t('sections.clarity.description')}
          items={t('sections.clarity.items', { returnObjects: true })}
          reasoning={t('sections.clarity.reasoning')}
        />

        <GuideSection
          icon="pixelated-image.svg"
          title={t('sections.quality.title')}
          description={t('sections.quality.description')}
          items={t('sections.quality.items', { returnObjects: true })}
          reasoning={t('sections.quality.reasoning')}
        />

        <GuideSection
          icon="pet-face.svg"
          title={t('sections.petFeatures.title')}
          description={t('sections.petFeatures.description')}
          items={t('sections.petFeatures.items', { returnObjects: true })}
          reasoning={t('sections.petFeatures.reasoning')}
        />

        <GuideSection
          icon="pet-tail.svg"
          title={t('sections.petMovement.title')}
          description={t('sections.petMovement.description')}
          items={t('sections.petMovement.items', { returnObjects: true })}
          reasoning={t('sections.petMovement.reasoning')}
        />

        <div className={`${theme.card.base} ${theme.card.hover} mt-8 bg-gradient-to-br from-[#0C3B2E] to-[#0C3B2E]/90 text-white`}>
          <div className="flex items-start gap-4 mb-6">
            <svg className="w-6 h-6 text-[#FFBA00] mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h4 className="text-xl font-semibold">{t('issues.title')}</h4>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="space-y-3">
              <h5 className="font-semibold text-[#FFBA00] mb-2">{t('issues.technical.title')}</h5>
              <ul className="list-disc pl-5 space-y-2 text-white/90">
                {t('issues.technical.items', { returnObjects: true }).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="space-y-3">
              <h5 className="font-semibold text-[#FFBA00] mb-2">{t('issues.environmental.title')}</h5>
              <ul className="list-disc pl-5 space-y-2 text-white/90">
                {t('issues.environmental.items', { returnObjects: true }).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="space-y-3">
              <h5 className="font-semibold text-[#FFBA00] mb-2">{t('issues.subject.title')}</h5>
              <ul className="list-disc pl-5 space-y-2 text-white/90">
                {t('issues.subject.items', { returnObjects: true }).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-white/20">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <h5 className="font-semibold text-[#FFBA00] mb-2">{t('issues.processing.title')}</h5>
                <ul className="list-disc pl-5 space-y-2 text-white/90">
                  {t('issues.processing.items', { returnObjects: true }).map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>

              <div className="space-y-3">
                <h5 className="font-semibold text-[#FFBA00] mb-2">{t('issues.animation.title')}</h5>
                <ul className="list-disc pl-5 space-y-2 text-white/90">
                  {t('issues.animation.items', { returnObjects: true }).map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className={`${theme.card.base} mt-8 border-[#FFBA00] bg-[#FFBA00]/5`}>
          <div className="flex items-start gap-4">
            <svg className="w-6 h-6 text-[#FFBA00] mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <h3 className="text-lg font-semibold mb-2 text-[#0C3B2E]">{t('actionRequirements.title')}</h3>
              <p className={theme.text.body}>{t('actionRequirements.description')}</p>
              <ul className="mt-4 space-y-2 list-disc pl-5">
                {t('actionRequirements.items', { returnObjects: true }).map((item, index) => (
                  <li key={index}>
                    <strong>{item.title}:</strong> {item.requirement}
                  </li>
                ))}
              </ul>
              <p className={`${theme.text.body} mt-4`}>{t('actionRequirements.note')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoGuideStep;